import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
let clipsCache = null;
let clipsPromise = null;
export function useMillieClips() {
  const [clips, setClips] = useState(clipsCache || {});
  useEffect(() => {
    if (clipsCache) return;
    clipsPromise = clipsPromise || api.get("/millie/clips").then(({ data }) => { clipsCache = data; return data; }).catch(() => ({}));
    clipsPromise.then((d) => setClips(d || {}));
  }, []);
  return clips;
}
export function invalidateMillieClips() { clipsCache = null; clipsPromise = null; }
const MOOD_CLIP = { happy: "idle", cosy: "idle", sleepy: "idle", love: "idle", wink: "idle", celebrate: "celebrate", cooking: "cooking", point: "celebrate", thinking: "thinking", idea: "thinking", surprised: "thinking", oops: "thinking" };
import { motion, AnimatePresence } from "framer-motion";
import { MILLIE, MOODS } from "../lib/millie";

const ENTRANCE = {
  celebrate: { scale: [0.9, 1.08, 1], rotate: [0, -4, 4, 0] },
  wave: { rotate: [0, -6, 6, -4, 0] },
  none: {},
};

/* Millie with a mood. Lifelike idle: slow breathing, gentle sway, soft weight shift. Expressions cross-fade (no hard swaps). */
const IDLE = {
  y: [0, -3, 0, -2, 0],
  rotate: [0, 1.2, 0, -1.2, 0],
  scaleY: [1, 1.012, 1, 1.008, 1],
  scaleX: [1, 0.995, 1, 0.997, 1],
};
const IDLE_T = { duration: 6.5, repeat: Infinity, ease: "easeInOut", times: [0, 0.3, 0.5, 0.8, 1] };

export function MillieAvatar({ size = 56, float = true, ring = true, src, mood = "happy", entrance = "none" }) {
  const img = src || MOODS[mood] || MOODS.happy;
  return (
    <motion.div
      animate={float ? { ...IDLE, ...ENTRANCE[entrance] } : { ...ENTRANCE[entrance] }}
      transition={float ? IDLE_T : { duration: 0.8 }}
      className={`shrink-0 rounded-full overflow-hidden bg-sorted-blush relative ${ring ? "border-4 border-white shadow-soft" : ""}`}
      style={{ width: size, height: size, transformOrigin: "50% 90%" }}
      data-mood={mood}
    >
      <AnimatePresence initial={false}>
        <motion.img key={img} src={img} alt="Millie, your meal companion"
          initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-full object-cover object-top" style={{ transform: "scale(1.15)", transformOrigin: "50% 20%" }} />
      </AnimatePresence>
    </motion.div>
  );
}

/* Half-body Millie hero. Breathes, sways and shifts weight like a person standing; expressions dissolve into each other. */
export function MillieHero({ mood = "happy", className = "", entrance = "wave", testId = "millie-hero" }) {
  const img = MOODS[mood] || MOODS.happy;
  const clips = useMillieClips();
  const clip = clips[MOOD_CLIP[mood] || "idle"];
  const videoRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    return registerHeroVideo(el);
  }, [clip?.url]);
  if (clip?.status === "ready" && clip.url) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className={`${className} overflow-hidden`} data-testid={testId} data-mood={mood} data-clip={MOOD_CLIP[mood] || "idle"}
        style={{ WebkitMaskImage: "radial-gradient(ellipse 78% 92% at 50% 45%, #000 60%, transparent 100%)", maskImage: "radial-gradient(ellipse 78% 92% at 50% 45%, #000 60%, transparent 100%)" }}>
        <video key={clip.url} ref={videoRef} src={`${API_BASE}${clip.url}`} autoPlay loop muted playsInline preload="auto" poster={img}
          className="w-full h-full object-cover object-bottom" data-testid={`${testId}-video`} />
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, ...IDLE, ...(entrance === "wave" ? { rotate: [0, -2.5, 2, -1, 0, 1.2, 0, -1.2, 0] } : {}) }}
      transition={{ opacity: { duration: 0.6 }, ...IDLE_T, rotate: entrance === "wave" ? { duration: 8, repeat: Infinity, ease: "easeInOut" } : IDLE_T }}
      className={`${className}`} style={{ transformOrigin: "50% 100%" }} data-testid={testId} data-mood={mood}
    >
      <AnimatePresence initial={false}>
        <motion.img key={img} src={img} alt="Millie"
          initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, position: "absolute" }} transition={{ duration: 0.7, ease: "easeInOut" }}
          className="w-full h-full object-contain object-bottom" />
      </AnimatePresence>
    </motion.div>
  );
}

// Shared audio so only one Millie speaks at a time.
let currentAudio = null;
const ttsCache = new Map();
// Mounted hero <video>s — speech waits for them so mouth and voice start together.
const heroVideos = new Set();
export const registerHeroVideo = (el) => { if (el) heroVideos.add(el); return () => heroVideos.delete(el); };
const ready = (el, min, ms) => new Promise((res) => {
  if (el.readyState >= min) return res();
  const done = () => { el.removeEventListener("canplay", done); el.removeEventListener("canplaythrough", done); res(); };
  el.addEventListener("canplay", done); el.addEventListener("canplaythrough", done); setTimeout(done, ms);
});
async function ttsUrl(plain) {
  let url = ttsCache.get(plain);
  if (!url) {
    const p = api.post("/millie/speak", { text: plain }).then(({ data }) => `${API_BASE}${data.url}`);
    ttsCache.set(plain, p);
    url = p;
  }
  return url;
}
// Warm the cache (and the browser's audio cache) so the next line plays instantly.
export function prefetchSpeech(text) {
  const plain = typeof text === "string" ? text.trim() : "";
  if (!plain) return;
  ttsUrl(plain).then((url) => { const a = new Audio(); a.preload = "auto"; a.src = url; }).catch(() => {});
}
export function stopSpeaking() { if (currentAudio) { currentAudio.pause(); currentAudio = null; } }
export async function speak(text, onState) {
  stopSpeaking();
  const plain = typeof text === "string" ? text : "";
  if (!plain.trim()) return;
  onState?.("loading");
  try {
    const url = await ttsUrl(plain.trim());
    const audio = new Audio(url);
    audio.preload = "auto";
    currentAudio = audio;
    audio.onplaying = () => onState?.("speaking");
    audio.onended = audio.onerror = () => { onState?.("idle"); if (currentAudio === audio) currentAudio = null; };
    await Promise.all([ready(audio, 4, 2500), ...[...heroVideos].map((v) => ready(v, 3, 2500))]);
    if (currentAudio !== audio) return;
    heroVideos.forEach((v) => { try { v.currentTime = 0; v.play().catch(() => {}); } catch { /* ignore */ } });
    await audio.play();
  } catch { onState?.("idle"); }
}

function bubbleText(children) {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(bubbleText).join("");
  if (children?.props?.children) return bubbleText(children.props.children);
  return "";
}

// A hand-lettered speech bubble in Millie's voice. Tap to hear Millie say it.
export function SpeechBubble({ children, className = "", tail = "bl", testId = "millie-bubble", speakable = true }) {
  const tailCls = tail === "br" ? "rounded-br-none" : tail === "bl" ? "rounded-bl-none" : "";
  const [state, setState] = useState("idle");
  const text = bubbleText(children);
  const onTap = () => { if (!speakable) return; if (state === "speaking" && currentAudio) { currentAudio.pause(); currentAudio = null; setState("idle"); return; } speak(text, setState); };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: state === "speaking" ? [1, 1.02, 1] : 1 }}
      transition={state === "speaking" ? { duration: 0.9, repeat: Infinity } : { type: "spring", stiffness: 300, damping: 22 }}
      data-testid={testId}
      data-speaking={state}
      onClick={onTap}
      role={speakable ? "button" : undefined}
      aria-label={speakable ? "Hear Millie say this" : undefined}
      className={`relative bg-white text-sorted-ink px-4 py-3 rounded-3xl ${tailCls} shadow-soft border-2 ${state === "speaking" ? "border-sorted-peach" : "border-[#FDECEF]"} ${speakable ? "cursor-pointer active:scale-[0.98]" : ""} ${className}`}
    >
      <span className="sr-only">Millie says: </span>
      <p className="font-hand text-xl leading-tight text-sorted-ink">{children}</p>
      {speakable && (
        <span className={`absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-sorted-peach border-2 border-white flex items-center justify-center text-sorted-ink ${state === "loading" ? "animate-pulse" : ""}`} data-testid={`${testId}-speak`} aria-hidden>
          {state === "speaking" ? <span className="flex items-end gap-[2px] h-3">{[0, 1, 2].map((i) => <motion.span key={i} className="w-[3px] bg-sorted-ink rounded-full" animate={{ height: ["30%", "100%", "40%"] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} />)}</span>
            : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /></svg>}
        </span>
      )}
    </motion.div>
  );
}

export default function MascotBubble({ caption, size = 56, pose, mood = "happy", testId = "millie-bubble" }) {
  return (
    <div className="flex items-end gap-3">
      <MillieAvatar size={size} src={pose ? MILLIE[pose] : undefined} mood={mood} />
      <SpeechBubble className="max-w-[74%]" testId={testId}>{caption}</SpeechBubble>
    </div>
  );
}
